import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import {
  LayoutDashboard,
  Boxes,
  KanbanSquare,
  BarChart3,
  Layers,
  Settings,
  Server,
  Container,
  Rocket,
  Users,
  ScrollText,
  ShieldCheck,
  Sliders,
  AppWindow,
  ChevronLeft,
  ChevronRight,
  Menu,
  Sun,
  Moon,
} from "lucide-react";
import { logout } from "../redux/actionCreator";
import { getSession } from "../utils/session";
import { getTheme, setTheme } from "../utils/theme";
import NotificationsBell from "./NotificationsBell";
import "./AdminLayout.scss";

const NAV = [
  { to: "/dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { to: "/apps", label: "My Apps", Icon: AppWindow },
  { to: "/projects", label: "Projects", Icon: Boxes },
  { to: "/boards", label: "Work Management", Icon: KanbanSquare },
  { to: "/dashboards", label: "Dashboards", Icon: BarChart3 },
  { to: "/workspaces", label: "Workspaces", Icon: Layers, roles: ["super_admin", "admin"] },
  { to: "/board-settings", label: "Board Defaults", Icon: Settings, roles: ["super_admin"] },
  { to: "/servers", label: "My Servers", Icon: Server },
  { to: "/docker", label: "Docker", Icon: Container },
  { to: "/deploy", label: "Deploy", Icon: Rocket },
  { to: "/employees", label: "Employees", Icon: Users, roles: ["super_admin"] },
  { to: "/permissions", label: "Permissions", Icon: ShieldCheck, roles: ["super_admin"] },
  { to: "/system-settings", label: "System Settings", Icon: Sliders, roles: ["super_admin"] },
  { to: "/logs", label: "Audit Logs", Icon: ScrollText, roles: ["super_admin", "admin"] },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  // Separate from `collapsed`: under 600px the sidebar is off-canvas and
  // this toggles it, rather than reusing the icons-only collapse state.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Seeded from the same source index.js used, so the button matches what
  // is already painted.
  const [theme, setThemeState] = useState(() => getTheme());
  const session = getSession() || {};
  const user = {
    email: session.email || "",
    name: session.name || session.email || "User",
  };
  const role = session.role || "employee";

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const items = NAV.filter((n) => !n.roles || n.roles.includes(role));

  return (
    <div className={`admin-shell ${collapsed ? "collapsed" : ""}`}>
      {/* First focusable element on the page: lets a keyboard user jump
          straight to content instead of traversing the whole nav. */}
      <a className="skip-link" href="#admin-content">Skip to content</a>
      <aside className={`admin-sidebar ${drawerOpen ? "open" : ""}`}>
        <div className="brand">
          <span className="brand-mark">SH</span>
          {!collapsed && <span className="brand-text">SECURITY</span>}
        </div>
        <nav className="nav" id="admin-nav">
          {items.map((it) => {
            const Icon = it.Icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                aria-label={it.label}
                title={collapsed ? it.label : undefined}
                onClick={() => setDrawerOpen(false)}
                className={({ isActive }) =>
                  `nav-item ${isActive ? "active" : ""}`
                }
              >
                <span className="nav-icon" aria-hidden="true">
                  <Icon size={18} strokeWidth={2} />
                </span>
                {!collapsed && <span className="nav-label">{it.label}</span>}
              </NavLink>
            );
          })}
        </nav>
        <button
          type="button"
          className="collapse-btn"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar">
          <button
            type="button"
            className="drawer-toggle"
            onClick={() => setDrawerOpen((o) => !o)}
            aria-label={drawerOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={drawerOpen}
            aria-controls="admin-nav"
          >
            <Menu size={18} />
          </button>
          <div className="topbar-title">Smarthatch Security Console</div>
          <div className="topbar-actions">
          <button
            type="button"
            className="theme-toggle"
            onClick={() => setThemeState(setTheme(theme === "dark" ? "light" : "dark"))}
            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <NotificationsBell />
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="light"
              className="user-pill"
              id="userMenu"
            >
              <span className="avatar">
                {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
              </span>
              <span className="user-name">
                {user.name || user.email || "User"}
              </span>
              <small className="user-role">{role}</small>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item disabled>{user.email}</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          </div>
        </header>

        <main className="admin-content" id="admin-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
